"use strict";
/**
 * Client-side password validators for the change-password flow.
 *
 * SOURCE OF TRUTH for the password policy is the Cognito user pool's
 * `Policies.PasswordPolicy` — configured in AWS console at
 *   User Pool → Sign-in experience → Password policy
 * NOT this file. These regexes are a mirror, hardcoded because the
 * pool's policy can only be read via the admin `DescribeUserPool` API
 * which requires IAM credentials the FE doesn't have.
 *
 * Verify any change against the live pool with:
 *   aws cognito-idp describe-user-pool \
 *     --user-pool-id <pool-id> \
 *     --query 'UserPool.Policies.PasswordPolicy'
 *
 * Currently mirrors: 8–25 chars, requires upper + lower + digit +
 * special, no whitespace. The 25-char upper bound is a FE-only
 * constraint (Cognito's default max is 256) — keep it if existing
 * sign-up flow uses the same cap, otherwise drop the length.max.
 *
 * If a future Cognito policy change drifts from this file, users will
 * see one of two symptoms:
 *   - FE rejects a password Cognito would accept   → tighten FE here
 *   - FE accepts, Cognito rejects with InvalidPasswordException
 *                                                  → loosen FE here
 * Either case → update this file + bump tests.
 *
 * Also ports the form-state helpers (touched-fields, submit-disabled)
 * from the old `apps/web-app/src/header/changePassword/helper.ts` so
 * both web and native UIs in the package share one validation surface.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.INITIAL_TOUCHED = exports.INITIAL_ERRORS = void 0;
exports.checkPasswordCriteria = checkPasswordCriteria;
exports.isValidPassword = isValidPassword;
exports.validateOldPassword = validateOldPassword;
exports.validateNewPassword = validateNewPassword;
exports.validateConfirmPassword = validateConfirmPassword;
exports.isSubmitDisabled = isSubmitDisabled;
exports.validateChangePasswordForm = validateChangePasswordForm;
const REGEX = {
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /\d/,
    noSpaces: /\s/,
    length: /^.{8,25}$/,
    special: /[!@#$%^&*()_+[\]{};':"\\|,.<>/?~]/,
    all: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9\s])[A-Za-z\d^$*.{}()[\]\\?!"@#%&/,><':;|_~`=+-]{8,25}$/,
};
/**
 * Per-criterion booleans for the criteria-checklist UI ("✓ has uppercase",
 * "✗ at least 1 number", etc.). Pure: re-runs on every keystroke.
 */
function checkPasswordCriteria(password) {
    return {
        uppercase: REGEX.uppercase.test(password),
        lowercase: REGEX.lowercase.test(password),
        number: REGEX.number.test(password),
        special: REGEX.special.test(password),
        length: REGEX.length.test(password),
        noSpaces: !REGEX.noSpaces.test(password),
    };
}
function isValidPassword(password) {
    return REGEX.all.test(password);
}
function validateOldPassword(oldPassword) {
    if (!oldPassword)
        return "oldPasswordError";
    if (!REGEX.length.test(oldPassword))
        return "oldPasswordCriteriaError";
    return "";
}
function validateNewPassword(newPassword) {
    if (!newPassword)
        return "newPasswordError";
    if (!isValidPassword(newPassword))
        return "passwordCriteriaError";
    return "";
}
function validateConfirmPassword(newPassword, confirmPassword) {
    if (!confirmPassword)
        return "confirmPasswordError";
    if (newPassword !== confirmPassword)
        return "confirmPasswordMatchError";
    return "";
}
exports.INITIAL_ERRORS = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
};
exports.INITIAL_TOUCHED = {
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
};
/**
 * Submit-button gate. Returns true (disabled) if ANY field would fail
 * its validator. Independent of `touched` — touched gates when errors
 * are SHOWN, not whether the form is submittable.
 */
function isSubmitDisabled(oldPassword, newPassword, confirmPassword) {
    return Boolean(validateOldPassword(oldPassword) ||
        validateNewPassword(newPassword) ||
        validateConfirmPassword(newPassword, confirmPassword));
}
/**
 * Run all three field validators and surface errors ONLY for touched
 * fields. Stops the form from screaming "required" on first render
 * before the user has had a chance to type anything.
 */
function validateChangePasswordForm(oldPassword, newPassword, confirmPassword, touched) {
    return {
        oldPassword: touched.oldPassword ? validateOldPassword(oldPassword) : "",
        newPassword: touched.newPassword ? validateNewPassword(newPassword) : "",
        confirmPassword: touched.confirmPassword
            ? validateConfirmPassword(newPassword, confirmPassword)
            : "",
    };
}
